// Single source of truth for every localStorage key the client uses.
//
// Rules:
// - Direct localStorage.* calls do not appear anywhere else in src/.
//   If you find yourself reaching for the raw global, add a namespace
//   below and use that instead.
// - Every namespace is created via single() or keyed() and exposes
//   the same {get, set, remove} surface, so callers don't need to
//   know whether their value is one key or a family.
// - Reads return null on parse failure; writes silently swallow
//   quota errors. This API is intended for non-essential cache-
//   shaped data — anything that MUST persist belongs in the DB.

// Build a namespace bound to a single physical key.
//   single('meet-pending-scan')         -> { get(), set(v), remove() }
//   single('meet-install-dismissed', { kind: 'string' })
function single(baseKey, { kind = 'json' } = {}) {
  return {
    baseKey,
    perKey: false,
    get() {
      try {
        const raw = localStorage.getItem(baseKey);
        if (raw == null) return null;
        return kind === 'json' ? JSON.parse(raw) : raw;
      } catch {
        return null;
      }
    },
    set(value) {
      try {
        const v = kind === 'json' ? JSON.stringify(value) : String(value);
        localStorage.setItem(baseKey, v);
      } catch {
        // quota / storage disabled — caller can't recover, drop silently.
      }
    },
    remove() {
      try { localStorage.removeItem(baseKey); } catch {}
    },
  };
}

// Build a namespace whose physical key is `${baseKey}-${subkey}`.
//   keyed('meet-draft')               -> { get(subkey), set(subkey, v), remove(subkey) }
//   keyed('meet-event-cache', { sep: ':' })  -> meet-event-cache:<id>
function keyed(baseKey, { kind = 'json', sep = '-' } = {}) {
  const physical = (subkey) => `${baseKey}${sep}${subkey}`;
  return {
    baseKey,
    perKey: true,
    sep,
    get(subkey) {
      try {
        const raw = localStorage.getItem(physical(subkey));
        if (raw == null) return null;
        return kind === 'json' ? JSON.parse(raw) : raw;
      } catch {
        return null;
      }
    },
    set(subkey, value) {
      try {
        const v = kind === 'json' ? JSON.stringify(value) : String(value);
        localStorage.setItem(physical(subkey), v);
      } catch {}
    },
    remove(subkey) {
      try { localStorage.removeItem(physical(subkey)); } catch {}
    },
  };
}

// The full key registry. Every namespace the app uses must be listed
// here so a) it gets a documented name, b) the sign-out sweep below
// knows whether to clear it, and c) future audits have one place to
// look.
//
// `clearOnSignOut` is the audit-correct list of namespaces that hold
// PII or per-user state and should be purged when a user signs out.
// (Existing behaviour from main.jsx: contactsCache is NOT in this
// list because it self-invalidates when a different user signs in.)
export const Storage = {
  contactsCache:    single('meet-contacts-cache'),
  profileMirror:    single('meet-profiles-db'),
  pendingScan:      single('meet-pending-scan',    { kind: 'string' }),
  autolinkDone:     single('meet-autolinked-user', { kind: 'string' }),
  installDismissed: single('meet-install-dismissed', { kind: 'string' }),
  activeEvent:      single('meet-active-event-id', { kind: 'string' }),
  profileDraft:     keyed('meet-draft'),
  eventCache:       keyed('meet-event-cache', { sep: ':' }),
};

// Namespaces whose contents are PII or per-user state. Listed here so
// clearOnSignOut() iterates and the sign-out caller doesn't have to
// know which strings to match.
const PURGE_ON_SIGN_OUT = [
  Storage.profileMirror,
  Storage.pendingScan,
  Storage.autolinkDone,
  Storage.profileDraft,
];

// Purge per-user data after a sign-out. Called from main.jsx's
// ApiAuthBridge. Matches only known keys (via baseKey for single,
// `${baseKey}${sep}*` for keyed) so unrelated origin-storage from
// other apps in dev is left alone.
export function clearOnSignOut() {
  try {
    const toDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      for (const ns of PURGE_ON_SIGN_OUT) {
        if (ns.perKey ? k.startsWith(ns.baseKey + ns.sep) : k === ns.baseKey) {
          toDelete.push(k);
          break;
        }
      }
    }
    for (const k of toDelete) localStorage.removeItem(k);
  } catch {}
}
